import React, { useCallback, useEffect, useState } from 'react';
import { MetaTags } from 'react-meta-tags';
import { useDispatch, useSelector } from 'react-redux';
import { useUrlSearchParams } from 'use-url-search-params';
import { Container, Row, Col, Table, Spinner, Badge, Input, Button } from 'reactstrap';
import { DashBreadcrumb } from '../../components/Common/Breadcrumb';
import { Toaster } from 'react-hot-toast';
import toast from 'react-hot-toast';
import ReactDrawer from 'react-drawer';
import 'react-drawer/lib/react-drawer.css';
import { DateRangePicker } from 'rsuite';
import moment from 'moment';
import axios from 'axios';
import UserAuthKey from '../../components/Common/UserAuthKey';
import userReducer, { setUserData } from '../../Redux/user/userSlice';
import DHPagination from '../../components/Pagination';
import { fetchDoList, fetchDoDetail } from './actions/complaintsDoActions';
import { SORTING_ORDER } from '../../constants/index';
import { DO_CREATE_ALLOWED_USER_ROLES, DO_CLOSED_ALLOWED_USER_ROLES } from '../../constants/roles';
import ComplaintsDoForm from './ComplaintsDoForm';

const ComplaintsDoList = () => {
  const dispatch = useDispatch();
  
  const authUser    = JSON.parse(localStorage.getItem('authUser') || '{}');
  const userRole    = authUser?.role_id || '';
  console.log('authUser:', userRole);
  const isDoUser    = DO_CREATE_ALLOWED_USER_ROLES.includes(userRole);
  const isDoManager = DO_CLOSED_ALLOWED_USER_ROLES.includes(userRole);
  const [paginationValue, setPaginationValue] = useUrlSearchParams({ page: 0 });
  const [limitValue,      setLimitValue]      = useUrlSearchParams({ limit: 10 });
  const [sortingValue]                        = useUrlSearchParams({ sort: '_date', order: SORTING_ORDER.ASC });
  const [searchFilter,    setSearchFilter]    = useUrlSearchParams({ search: '' });
  const [statusFilter,    setStatusFilter]    = useUrlSearchParams({ status: 'All' });
  const [advancedFilter,  setAdvancedFilter]  = useState({});

  // Filter bar local state
  const [searchInputValue,  setSearchInputValue]  = useState('');
  const [statusInputValue,  setStatusInputValue]  = useState('All');
  const [dateRangeValue,    setDateRangeValue]    = useState(null);
  const [advanceFilterData, setAdvanceFilterData] = useState({});

  const [isDataLoading, setDataLoading] = useState(false);

  const { doList } = useSelector((state) => state.complaintsDo);

  const doData = Array.isArray(doList?.data?.orderList)
    ? doList.data.orderList
    : [];

  let currentCountry = localStorage.getItem('WEBSITE_COUNTRY');

  const loadList = useCallback(async (params) => {
    setDataLoading(true);
    try {
      await fetchDoList({ params, dispatch });
    } finally {
      setDataLoading(false);
    }
  }, [dispatch]);

  useEffect(() => {
    const delay = setTimeout(() => {
      loadList({
        start:      Number(paginationValue.page * limitValue.limit),
        limit:      Number(limitValue.limit),
        sort:       { value: sortingValue.sort, order: Number(sortingValue.order) },
        search_qry: searchFilter.search,
        status:     statusFilter.status === 'All' ? '' : statusFilter.status,
        orderBy:    'id',
        country:    currentCountry,
        date_from:  advancedFilter?.frmDt || '',
        date_to:    advancedFilter?.toDt  || '',
      });
    }, 300);
    return () => clearTimeout(delay);
  }, [paginationValue.page, limitValue, searchFilter, statusFilter, advancedFilter]);

  let pageIndex = doData.length > 0 ? Number(paginationValue.page * limitValue.limit) : 0;

  // ── Filter handlers ────────────────────────────────────────────────────────
  const disableFutureDates = (date) => date.getTime() > new Date().getTime();

  const handleDateRangeChange = (dates) => {
    if (dates?.[0] && dates?.[1]) {
      setAdvanceFilterData((prev) => ({
        ...prev,
        frmDt: moment(dates[0]).format('YYYY-MM-DD'),
        toDt:  moment(dates[1]).format('YYYY-MM-DD'),
      }));
      setDateRangeValue(dates);
    }
  };

  const handleDateRangeClear = () => {
    setAdvanceFilterData((prev) => ({ ...prev, frmDt: '', toDt: '' }));
    setDateRangeValue(null);
  };

  const handleApplyFilters = () => {
    setSearchFilter({ search: searchInputValue });
    setStatusFilter({ status: statusInputValue });
    setAdvancedFilter(advanceFilterData);
    setPaginationValue({ page: 0 });
  };

  const handleResetFilters = () => {
    setSearchInputValue('');
    setStatusInputValue('All');
    setDateRangeValue(null);
    setAdvanceFilterData({});
    setSearchFilter({ search: '' });
    setStatusFilter({ status: 'All' });
    setAdvancedFilter({});
    setPaginationValue({ page: 0 });
  };

  // ── Drawer ─────────────────────────────────────────────────────────────────
  const [drawerOpen,    setDrawerOpen]    = useState(false);
  const [drawerData,    setDrawerData]    = useState(null);
  const [drawerLoading, setDrawerLoading] = useState(false);
  const [itemStatuses,  setItemStatuses]  = useState({});
  const [updatingItem,  setUpdatingItem]  = useState(null);

  const handleViewDrawer = async (row) => {
    setDrawerData(null);
    setDrawerOpen(true);
    setDrawerLoading(true);
    try {
      const res = await fetchDoDetail({ receipt_no: row.SerialNo, dispatch });
      const d   = res?.data ?? res;
      setDrawerData(d || null);
    } catch {
      setDrawerData(null);
    } finally {
      setDrawerLoading(false);
    }
  };

  const handleItemStatusChange = (itemId, value) => {
    setItemStatuses((prev) => ({ ...prev, [itemId]: value }));
  };

  const handleUpdateItemStatus = async (item) => {
    const newStatus = itemStatuses[item.id];
    if (!newStatus || newStatus === item.status) return;
    const apiUrl      = process.env.REACT_APP_OMS_API;
    const bearerToken = UserAuthKey();
    setUpdatingItem(item.id);
    try {
      await axios({
        method:  'POST',
        url:     `${apiUrl}v1/orders/complaints-do/update-status`,
        headers: { Authorization: `Bearer ${bearerToken}`, 'Content-Type': 'application/json' },
        data:    { items: [{ complaint_do_id: item.id, sku: item.sku, status: newStatus }] },
      });
      setDrawerData((prev) => ({
        ...prev,
        items: prev.items.map((i) => i.id === item.id ? { ...i, status: newStatus } : i),
      }));
      setItemStatuses((prev) => { const n = { ...prev }; delete n[item.id]; return n; });
      toast.success('Status updated.');
      loadList({
        start:      Number(paginationValue.page * limitValue.limit),
        limit:      Number(limitValue.limit),
        sort:       { value: sortingValue.sort, order: SORTING_ORDER.ASC },
        search_qry: searchFilter.search,
        status:     statusFilter.status === 'All' ? '' : statusFilter.status,
        orderBy:    'id',
        country:    currentCountry,
        date_from:  advancedFilter?.frmDt || '',
        date_to:    advancedFilter?.toDt  || '',
      });
    } catch (error) {
      if (error?.response?.status === 401) {
        dispatch(setUserData(null));
        window.location.replace('/logout');
      } else {
        toast.error('Failed to update status.');
      }
    } finally {
      setUpdatingItem(null);
    }
  };

  // ── Download ───────────────────────────────────────────────────────────────
  const [downloadingId,  setDownloadingId]  = useState(null);
  const [formDrawerOpen, setFormDrawerOpen] = useState(false);

  const handleFormSuccess = () => {
    setFormDrawerOpen(false);
    loadList({
      start:      Number(paginationValue.page * limitValue.limit),
      limit:      Number(limitValue.limit),
      sort:       { value: sortingValue.sort, order: SORTING_ORDER.ASC },
      search_qry: searchFilter.search,
      status:     statusFilter.status === 'All' ? '' : statusFilter.status,
      orderBy:    'id',
      country:    currentCountry,
      date_from:  advancedFilter?.frmDt || '',
      date_to:    advancedFilter?.toDt  || '',
    });
  };

  const handleDownload = async (row) => {
    if (!row?.SerialNo) return;
    const apiUrl      = process.env.REACT_APP_OMS_API;
    const bearerToken = UserAuthKey();
    setDownloadingId(row.SerialNo);
    try {
      const response = await axios({
        method:       'GET',
        url:          `${apiUrl}v1/orders/complaints-do/download?receipt_no=${row.SerialNo}`,
        headers:      { Authorization: `Bearer ${bearerToken}` },
        responseType: 'blob',
      });
      const url  = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href  = url;
      link.setAttribute('download', `complaint-do-${row.SerialNo}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      if (error?.response?.status === 401) {
        dispatch(setUserData(null));
        window.location.replace('/logout');
      } else {
        toast.error('Download failed. Please try again.');
      }
    } finally {
      setDownloadingId(null);
    }
  };

  // ── Export ────────────────────────────────────────────────────────────────
  const [exportDateRange,  setExportDateRange]  = useState(null);
  const [isExporting,      setIsExporting]      = useState(false);

  const handleExport = async () => {
    if (!exportDateRange || exportDateRange.length !== 2) {
      toast.error('Please select a date range before exporting.');
      return;
    }
    const omsServiceapiUrl = process.env.REACT_APP_OMS_NEST_API;
    const authUser         = JSON.parse(localStorage.getItem('authUser') || '{}');
    const fromDate         = moment(exportDateRange[0]).format('YYYY-MM-DD');
    const toDate           = moment(exportDateRange[1]).format('YYYY-MM-DD');
    const payload = {
      exportType: 'DO',
      dateFrom:   fromDate,
      dateTo:     toDate,
      country:    currentCountry || 'ae',
      userId:     authUser?.user_id || authUser?.id || '',
      email:      authUser?.user_email || authUser?.email || '',
      name:       authUser?.user_name  || authUser?.name  || '',
    };
    setIsExporting(true);
    try {
      const response = await axios.post(`${omsServiceapiUrl}/automation/do-report`, payload, {
        headers: { 'Content-Type': 'application/json' },
      });
      if (response.data.status === 'success') {
        toast.success(response.data.message || 'Report will be sent to your email.');
      } else {
        toast.error('Export failed. Please try again.');
      }
    } catch (error) {
      if (error?.response?.status === 401) {
        dispatch(setUserData(null));
        window.location.replace('/logout');
      } else {
        toast.error('Export failed. Please try again.');
      }
    } finally {
      setIsExporting(false);
    }
  };

  // ── Status badge ───────────────────────────────────────────────────────────
  const StatusBadge = ({ status }) => {
    const s = (status || '').toLowerCase();
    if (s === 'closed')   return <span className="badge bg-soft-success text-success text-capitalize">{status}</span>;
    if (s === 'canceled') return <span className="badge bg-soft-danger text-danger text-capitalize">{status}</span>;
    if (s === 'received') return <span className="badge bg-soft-info text-dark me-2 text-capitalize">{status}</span>;
    const colorMap = { pending: 'warning', approved: 'success', rejected: 'danger', completed: 'info' };
    return <Badge color={colorMap[s] || 'secondary'} className="badge badge-pill text-capitalize">{status || '—'}</Badge>;
  };

  const formDrawerStyle = `
    .createDoDrawer .react-drawer-drawer {
      width: 50% !important;
    }
    .rs-picker-daterange-menu {
      z-index: 9999 !important;
    }
    .rs-picker-menu {
      overflow: visible !important;
    }
  `;

  return (
    <React.Fragment>
      <style>{formDrawerStyle}</style>
      <div className="page-content refundOrder">
        <MetaTags><title>Manage DO List</title></MetaTags>
        <Container fluid>
          <Row>
            <Col>
              <DashBreadcrumb title="Complaints" breadcrumbItem="Manage DO List" />
            </Col>
            <Col xl={6}>
              <Row className="align-items-end">
                <Col xl={12} className="textAlignRigh">
                  <div className="DashbordFliter">
                    <div className="InputBg me-2 d-inline-block">
                      <DateRangePicker
                        placeholder="Select Date Range"
                        onChange={setExportDateRange}
                        value={exportDateRange}
                        disabledDate={disableFutureDates}
                        format="yyyy-MM-dd"
                        container={() => document.body}
                        placement="bottomEnd"
                      />
                    </div>
                    <button
                      type="button"
                      className="btn btn-dark me-2"
                      onClick={handleExport}
                      disabled={isExporting}
                    >
                      {isExporting ? 'Exporting...' : <><i className="bx bx-export me-1"></i>Export</>}
                    </button>
                    {(isDoUser) && (
                      <button type="button" className="btn btn-dark" onClick={() => setFormDrawerOpen(true)}>
                        Create Request
                      </button>
                    )}
                  </div>
                </Col>
              </Row>
            </Col>
          </Row>

          {/* ── Filter Bar ── */}
          <div className="p-3 bg-soft-light border-1 border-light mb-3" style={{ borderStyle: 'dashed' }}>
            <Row>
              <Col className="d-flex flex-row align-items-center gap-2 flex-wrap">
                <div className="position-relative" style={{ minWidth: 220 }}>
                  <Input
                    type="text"
                    placeholder="Search serial no, SO number..."
                    value={searchInputValue}
                    onChange={(e) => setSearchInputValue(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleApplyFilters()}
                    className="form-control"
                  />
                  <span className="p-2 text-light position-absolute top-50 end-0 translate-middle-y">
                    <i className="bx bx-search-alt font-size-16 align-middle"></i>
                  </span>
                </div>
                <div style={{ minWidth: 160 }}>
                  <select className="form-select" value={statusInputValue} onChange={(e) => setStatusInputValue(e.target.value)}>
                    <option value="All">All Status</option>
                    <option value="pending">Pending</option>
                    <option value="received">Received</option>
                    <option value="completed">Completed</option>
                    <option value="closed">Closed</option>
                    <option value="canceled">Canceled</option>
                  </select>
                </div>
                <div>
                  <DateRangePicker
                    size="lg"
                    placement="auto"
                    placeholder="Select Date Range"
                    disabledDate={disableFutureDates}
                    onChange={handleDateRangeChange}
                    onClean={handleDateRangeClear}
                    value={dateRangeValue}
                  />
                </div>
                <Button color="secondary" onClick={handleApplyFilters}>
                  <i className="bx bx-search-alt font-size-16 align-middle"></i>
                </Button>
                <Button outline color="danger" onClick={handleResetFilters}>
                  <i className="bx bx-revision font-size-16 align-middle"></i>
                </Button>
              </Col>
            </Row>
            <Row>
              <Col className="mt-2">
                <p className="m-0 d-flex gap-2 align-items-center">
                  {searchFilter?.search ? (
                    <span className="border px-1 bg-white rounded fs-10">
                      Keyword: {searchFilter.search}{' '}
                      <i className="mdi mdi-close" style={{ cursor: 'pointer' }} onClick={() => { setSearchFilter({ search: '' }); setSearchInputValue(''); }} />
                    </span>
                  ) : null}
                  {statusFilter?.status && statusFilter.status !== 'All' ? (
                    <span className="border px-1 bg-white rounded fs-10">
                      Status: {statusFilter.status}{' '}
                      <i className="mdi mdi-close" style={{ cursor: 'pointer' }} onClick={() => { setStatusFilter({ status: 'All' }); setStatusInputValue('All'); }} />
                    </span>
                  ) : null}
                  {advanceFilterData?.frmDt ? (
                    <span className="border px-1 bg-white rounded fs-10">
                      Date: {advanceFilterData.frmDt} to {advanceFilterData.toDt}{' '}
                      <i className="mdi mdi-close" style={{ cursor: 'pointer' }} onClick={() => { handleDateRangeClear(); setAdvancedFilter((p) => ({ ...p, frmDt: '', toDt: '' })); }} />
                    </span>
                  ) : null}
                </p>
              </Col>
            </Row>
          </div>

          {/* ── Table ── */}
          <div className="dt-table">
            <div className="table-responsive">
              <Table className="OrderListTable table mb-0">
                <thead>
                  <tr>
                    <th>SI.No</th>
                    <th nowrap="nowrap">Date</th>
                    <th nowrap="nowrap">Serial No</th>
                    <th nowrap="nowrap">SO Number</th>
                    <th nowrap="nowrap">Complaint No</th>
                    <th nowrap="nowrap">Customer Details</th>
                    <th nowrap="nowrap">Created By</th>
                    <th>Status</th>
                    <th nowrap="nowrap">Action</th>
                  </tr>
                </thead>
                {isDataLoading ? (
                  <>
                    <Spinner className="ms-2 sploader" color="primary" />
                    <tbody>
                      {[...Array(3)].map((_, i) => (
                        <tr key={i}>
                          {[...Array(9)].map((__, j) => (
                            <td key={j}><p className="placeholder-glow"><span className="placeholder col-8"></span></p></td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </>
                ) : doData.length > 0 ? (
                  <tbody>
                    {doData.map((row, index) => {
                      pageIndex++;
                      const orderDt = moment(row.Date).format('Do MMM, YYYY');
                      const orderTm = moment(row.Date).format('h:mm:ss a');
                      return (
                        <tr key={row.complaintsId || index}>
                          <th scope="row">{pageIndex}</th>
                          <td>
                            <span className="fs-12">{orderDt}</span>
                            <p className="fs-12 mb-0">{orderTm}</p>
                          </td>
                          <td>
                            <span className="badge bg-soft-dark text-dark fs-12">{row.SerialNo || '—'}</span>
                          </td>
                          <td>
                            <span className="fs-12 fw-semibold">{row.SoNumber || '—'}</span>
                          </td>
                          <td>
                            <span className="fs-12">{row.complaint_no || row.ComplaintNo || '—'}</span>
                          </td>
                          <td>
                            <span className="fs-12 fw-semibold d-block">{row.FullName || '—'}</span>
                            <span className="fs-12 d-block">{row.PhoneNumber || ''}</span>
                            <span className="fs-12 d-block text-muted">{row.Address || ''}</span>
                          </td>
                          <td className="fs-12">{row.CreatedBy || row.created_by || '—'}</td>
                          <td><StatusBadge status={row.Status} /></td>
                          <td>
                            <div className="action-flex-container">
                              <span
                                className="btn btn-sm btn-outline-warning waves-effect waves-light"
                                title="View Items"
                                onClick={() => handleViewDrawer(row)}
                              >
                                <i className="mdi mdi-eye-outline"></i>
                              </span>
                              {(row.Status || '').toLowerCase() !== 'canceled' && (
                                <span
                                  className="btn btn-sm btn-outline-success waves-effect waves-light"
                                  title="Download DO"
                                  onClick={() => handleDownload(row)}
                                  style={{ opacity: downloadingId === row.SerialNo ? 0.6 : 1, pointerEvents: downloadingId === row.SerialNo ? 'none' : 'auto' }}
                                >
                                  {downloadingId === row.SerialNo
                                    ? <i className="mdi mdi-loading mdi-spin"></i>
                                    : <i className="mdi mdi-download-outline"></i>
                                  }
                                </span>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                ) : (
                  <tbody>
                    <tr>
                      <td colSpan={9} className="text-center py-4 text-muted">No Records found</td>
                    </tr>
                  </tbody>
                )}
              </Table>
            </div>
            {doData.length > 0 && (
              <DHPagination
                totalPages={doList?.data?.recordsTotal || doData.length}
                currentPage={paginationValue.page}
                setPaginationValue={setPaginationValue}
                setLimitValue={setLimitValue}
              />
            )}
          </div>
        </Container>
      </div>

      {/* ── Items Drawer ── */}
      <ReactDrawer open={drawerOpen} position="right" onClose={() => setDrawerOpen(false)} style={{ width: 480 }}>
        <div style={{ display: 'flex', flexDirection: 'column', height: '100%', background: '#fff' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 20px', borderBottom: '1px solid #dee2e6', background: '#f8f9fa' }}>
            <div>
              <div style={{ fontSize: 15, fontWeight: 700, color: '#343a40' }}>DO Items</div>
              {drawerData && (
                <div style={{ fontSize: 12, color: '#6c757d', marginTop: 2 }}>
                  Serial No: <strong>{drawerData.serial_no}</strong> &nbsp;|&nbsp; SO: <strong>{drawerData.so_number}</strong>
                  {drawerData.complaint_no && <>&nbsp;|&nbsp; Complaint No: <strong>{drawerData.complaint_no}</strong></>}
                </div>
              )}
            </div>
            <span onClick={() => setDrawerOpen(false)} style={{ cursor: 'pointer', fontSize: 20, color: '#6c757d' }}>
              <i className="mdi mdi-close"></i>
            </span>
          </div>
          <div style={{ flex: 1, overflowY: 'auto', padding: 20 }}>
            {drawerLoading ? (
              <div style={{ textAlign: 'center', paddingTop: 60 }}><Spinner color="primary" /></div>
            ) : !drawerData ? (
              <div style={{ textAlign: 'center', paddingTop: 60, color: '#adb5bd', fontSize: 14 }}>No data found.</div>
            ) : (
              <>
                <div style={{ background: '#f8f9fa', borderRadius: 6, padding: '12px 16px', marginBottom: 16, fontSize: 13 }}>
                  <div style={{ fontWeight: 700, color: '#343a40', marginBottom: 6 }}>{drawerData.full_name || '—'}</div>
                  <div style={{ color: '#6c757d', marginBottom: 4 }}><i className="bx bx-phone me-1"></i>{drawerData.phone_number || '—'}</div>
                  <div style={{ color: '#6c757d' }}><i className="bx bx-map me-1"></i>{drawerData.address || '—'}</div>
                </div>
                <div style={{ fontSize: 12, fontWeight: 700, color: '#495057', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 10 }}>
                  Items ({Array.isArray(drawerData.items) ? drawerData.items.length : 0})
                </div>
                {Array.isArray(drawerData.items) && drawerData.items.length > 0 ? (
                  drawerData.items.map((item, idx) => {
                    const currentStatus = itemStatuses[item.id] ?? item.status ?? 'pending';
                    const isUpdating    = updatingItem === item.id;
                    const isDirty       = itemStatuses[item.id] && itemStatuses[item.id] !== item.status;
                    return (
                      <div key={item.id || idx} style={{ border: '1px solid #e3e6ed', borderRadius: 8, marginBottom: 12, overflow: 'hidden', boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>
                        {/* Card Header */}
                        <div style={{ background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)', padding: '10px 14px', borderBottom: '1px solid #e3e6ed', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                          <div>
                            <span style={{ background: '#343a40', color: '#fff', padding: '2px 10px', borderRadius: 4, fontSize: 11, fontWeight: 700, letterSpacing: '0.04em' }}>{item.sku}</span>
                            {item.name && (
                              <div style={{ fontSize: 12, color: '#343a40', fontWeight: 600, marginTop: 5, lineHeight: 1.4 }}>{item.name}</div>
                            )}
                          </div>
                          <StatusBadge status={item.status} />
                        </div>
                        {/* Card Body */}
                        <div style={{ padding: '10px 14px' }}>
                          <div style={{ display: 'flex', gap: 16, marginBottom: item.remarks ? 10 : 0 }}>
                            <div style={{ background: '#f0f4ff', borderRadius: 6, padding: '6px 12px', textAlign: 'center', minWidth: 56 }}>
                              <div style={{ fontSize: 10, color: '#6c757d', fontWeight: 600, textTransform: 'uppercase' }}>Qty</div>
                              <div style={{ fontSize: 16, fontWeight: 700, color: '#1976d2' }}>{item.qty}</div>
                            </div>
                            <div style={{ background: item.is_returnable === '1' || item.is_returnable === 1 ? '#f0faf4' : '#fff5f5', borderRadius: 6, padding: '6px 12px', textAlign: 'center', minWidth: 80 }}>
                              <div style={{ fontSize: 10, color: '#6c757d', fontWeight: 600, textTransform: 'uppercase' }}>Returnable</div>
                              <div style={{ fontSize: 13, fontWeight: 700, color: item.is_returnable === '1' || item.is_returnable === 1 ? '#198754' : '#dc3545', marginTop: 2 }}>
                                {item.is_returnable === '1' || item.is_returnable === 1 ? 'Yes' : 'No'}
                              </div>
                            </div>
                          </div>
                          {item.remarks && (
                            <div style={{ marginTop: 8, background: '#fffbf0', border: '1px solid #ffe69c', borderRadius: 6, padding: '7px 12px', fontSize: 12, color: '#6c757d', lineHeight: 1.5 }}>
                              <span style={{ fontWeight: 600, color: '#856404' }}><i className="bx bx-comment-detail me-1"></i>Remarks:</span> {item.remarks}
                            </div>
                          )}
                        </div>
                        <div style={{ display: 'flex', gap: 8, alignItems: 'center', borderTop: '1px solid #e9ecef', padding: '10px 14px', background: '#fafbfc' }}>
                          {(item.status || '').toLowerCase() === 'canceled' ? (
                            <span style={{ fontSize: 12, color: '#842029', fontStyle: 'italic' }}>Status cannot be changed after cancellation.</span>
                          ) : !isDoUser && !isDoManager ? (
                            <span style={{ fontSize: 12, color: '#6c757d', fontStyle: 'italic' }}>No permission to update status.</span>
                          ) : (
                          <>
                          <select
                            value={currentStatus}
                            onChange={(e) => handleItemStatusChange(item.id, e.target.value)}
                            style={{ flex: 1, padding: '5px 10px', fontSize: 12, border: '1px solid #ced4da', borderRadius: 4, color: '#495057', background: '#fff' }}
                          >
                            <option value="pending">Pending</option>
                            {(isDoUser) && <option value="received">Received</option>}
                            {(isDoUser) && <option value="canceled">Canceled</option>}
                            {isDoManager && <option value="closed">Closed</option>}
                          </select>
                          <button
                            onClick={() => handleUpdateItemStatus(item)}
                            disabled={!isDirty || isUpdating}
                            style={{
                              padding: '5px 14px', fontSize: 12, fontWeight: 600,
                              border: 'none', borderRadius: 4,
                              cursor: isDirty && !isUpdating ? 'pointer' : 'not-allowed',
                              background: isDirty ? '#0d6efd' : '#e9ecef',
                              color: isDirty ? '#fff' : '#adb5bd',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {isUpdating ? 'Saving...' : 'Update'}
                          </button>
                          </>
                          )}
                        </div>
                      </div>
                    );
                  })
                ) : (
                  <div style={{ textAlign: 'center', padding: 32, color: '#adb5bd', fontSize: 13 }}>No items found.</div>
                )}
              </>
            )}
          </div>
        </div>
      </ReactDrawer>

      {/* ── Create DO Drawer ── */}
      <div className="createDoDrawer">
        <ReactDrawer open={formDrawerOpen} position="right" onClose={() => setFormDrawerOpen(false)}>
          <div style={{ display: 'flex', flexDirection: 'column', height: '100%', background: '#fff' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 20px', borderBottom: '1px solid #dee2e6', background: '#f8f9fa' }}>
              <div style={{ fontSize: 16, fontWeight: 700, color: '#343a40' }}>Create Request</div>
              <span onClick={() => setFormDrawerOpen(false)} style={{ cursor: 'pointer', fontSize: 20, color: '#6c757d' }}>
                <i className="mdi mdi-close"></i>
              </span>
            </div>
            <div style={{ flex: 1, overflowY: 'auto', padding: 20 }}>
              <ComplaintsDoForm
                onClose={() => setFormDrawerOpen(false)}
                onSuccess={handleFormSuccess}
              />
            </div>
          </div>
        </ReactDrawer>
      </div>

      <Toaster position="top-right" reverseOrder={false} />
    </React.Fragment>
  );
};

export default ComplaintsDoList;
